// DANFE is untrusted evidence, never a fiscal XML, supplier entitlement, or stock receipt.
import {validNfeKey,validCnpj} from './nfeIntake.js';
const digits=value=>String(value??'').replace(/\D/g,'');
export const MAX_DANFE_FILES=3;
export const MAX_DANFE_BYTES=8*1024*1024;
export function validateDanfeFiles(files){
  if(!Array.isArray(files)||files.length<1||files.length>MAX_DANFE_FILES)throw Error('Escolha até 3 fotos ou um PDF do DANFE.');
  const allowed=new Set(['image/jpeg','image/png','image/webp','application/pdf']);
  if(files.some(f=>!f||!allowed.has(String(f.type||'').toLowerCase())||!f.size||f.size>MAX_DANFE_BYTES))
    throw Error('Use fotos JPG/PNG/WebP ou PDF, cada arquivo até 8 MB.');
  if(files.reduce((sum,f)=>sum+f.size,0)>MAX_DANFE_BYTES)throw Error('Total máximo de 8 MB por leitura.');
  if(files.some(f=>f.type==='application/pdf')&&files.length!==1)throw Error('Envie o PDF sozinho; fotos podem ser analisadas em conjunto.');
  return files;
}
export function parseDanfeDecimal(value){
  const s=String(value??'').trim().replace(/\s/g,'');
  if(!s||s.length>28||!/^[0-9.,]+$/.test(s))return null;
  // pt-BR 1.234,50 and international 1234.50; no ambiguous thousand separators.
  let normalized=s;
  if(s.includes(',')&&s.includes('.')){
    if(s.lastIndexOf(',')>s.lastIndexOf('.'))normalized=s.replace(/\./g,'').replace(',','.');
    else normalized=s.replace(/,/g,'');
  }else if(s.includes(','))normalized=s.replace(',','.');
  if(!/^\d+(\.\d{1,6})?$/.test(normalized))return null;
  const number=Number(normalized);
  return Number.isFinite(number)&&number>=0&&number<=1e9?number:null;
}
export function normalizeDanfeDraft(payload){
  if(!payload||typeof payload!=='object')throw Error('Leitura do DANFE indisponível.');
  const raw=payload.suggestion??payload;
  const key=digits(raw.access_key||'');
  const supplierCnpj=digits(raw.supplier_cnpj||'');
  const recipientCnpj=digits(raw.recipient_cnpj||'');
  const lines=Array.isArray(raw.items)?raw.items.slice(0,100):[];
  return {
    accessKey:validNfeKey(key)&&key.slice(20,22)==='55'?key:'',
    supplierCnpj:validCnpj(supplierCnpj)?supplierCnpj:'',
    recipientCnpj:validCnpj(recipientCnpj)?recipientCnpj:'',
    supplierName:String(raw.supplier_name||'').slice(0,130),
    totalItemsDeclared:parseDanfeDecimal(raw.total_items_declared),
    allItemsVisible:raw.all_items_visible===true,
    pagesRead:Number.isInteger(raw.pages_read)?Math.max(0,Math.min(raw.pages_read,50)):0,
    confidence:Math.max(0,Math.min(100,Number(raw.confidence)||0)),
    warnings:(Array.isArray(raw.warnings)?raw.warnings:[]).map(x=>String(x||'').slice(0,180)).slice(0,12),
    items:lines.map((item,i)=>({
      id:i+1,name:String(item.description||'').trim().slice(0,180),
      supplierCode:String(item.supplier_code||'').trim().slice(0,70),
      barcode:/^\d{8,14}$/.test(digits(item.gtin||''))?digits(item.gtin):'',
      unit:String(item.unit||'').toUpperCase().replace(/[^A-Z0-9]/g,'').slice(0,12),
      quantity:parseDanfeDecimal(item.quantity),
      unitPrice:parseDanfeDecimal(item.unit_price),
      total:parseDanfeDecimal(item.total),
      confidence:Math.max(0,Math.min(100,Number(item.confidence)||0)),
      confirmed:false,productId:'',matchStatus:'unmatched'
    }))
  };
}
export function resolveDanfeCatalogItem(item,products){
  if(!item?.barcode)return{productId:'',matchStatus:'unmatched'};
  const matches=(products||[]).filter(p=>p.barcode===item.barcode);
  if(matches.length!==1)return{productId:'',matchStatus:matches.length?'ambiguous':'unmatched'};
  const product=matches[0];
  if(product.active===false||!product.track_inventory)return{productId:'',matchStatus:'inactive'};
  if(String(product.unit||'').toUpperCase()!==item.unit)return{productId:'',matchStatus:'unit_mismatch'};
  if(item.quantity!=null&&!product.allow_fractional&&!Number.isInteger(item.quantity))
    return{productId:'',matchStatus:'fractional'};
  return{productId:product.id,matchStatus:'candidate'};
}
export function danfeDraftSafety(draft,{recipientCnpj='',scannedKey=''}={}){
  const issues=[];
  if(!draft)issues.push('DANFE não analisado.');
  else {
    if(!validNfeKey(draft.accessKey)||draft.accessKey.slice(20,22)!=='55')issues.push('Chave de NF-e modelo 55 não reconhecida.');
    if(scannedKey&&draft.accessKey!==scannedKey)issues.push('Chave do DANFE não corresponde à chave escaneada.');
    if(!validCnpj(draft.recipientCnpj))issues.push('CNPJ do destinatário não reconhecido.');
    if(!recipientCnpj)issues.push('CNPJ destinatário da empresa ainda não configurado pelo gestor.');
    if(recipientCnpj&&draft.recipientCnpj!==recipientCnpj)issues.push('CNPJ diferente da empresa ativa.');
    if(!draft.allItemsVisible)issues.push('Nem todos os itens/páginas foram confirmados.');
    if(!draft.items?.length)issues.push('Nenhum item foi reconhecido.');
    if(draft.items?.some(i=>!i.name||i.quantity===null||i.quantity<=0||i.unitPrice===null))
      issues.push('Existem itens incompletos ou quantidades inválidas.');
    if(draft.items?.some(i=>!i.confirmed||!i.productId))
      issues.push('Itens ainda exigem conferência e vínculo manual ao catálogo.');
  }
  issues.push('DANFE sem XML autenticado: lançamento automático bloqueado.');
  return issues;
}
export function serializeDanfeDraftCSV(draft){
  const escape=value=>{ const cell=String(value??''); const safe=/^[=+@\-\t\r]/.test(cell.trimStart()) ? '\u0027'+cell : cell; return '"'+safe.replace(/"/g,'""')+'"'; };
  const head=['Código do fornecedor','Descrição','GTIN','Unidade','Quantidade','Preço unitário','Total','SKU vinculado','Conferido'];
  return '\uFEFF'+[head,...(draft?.items||[]).map(i=>[
    i.supplierCode,i.name,i.barcode,i.unit,i.quantity,i.unitPrice,i.total,i.productId,i.confirmed?'SIM':'NÃO'
  ])].map(row=>row.map(escape).join(';')).join('\r\n');
}
